import { useState, useEffect, useCallback } from "react";
import {
  Briefcase,
  Copy,
  Check,
  AlertCircle,
  Power,
  CreditCard,
  Clock,
  Gift,
  Info,
  ChevronDown,
  ChevronUp,
  RotateCcw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MP_STATUS_META = {
  not_connected: {
    label: "No conectado",
    tone: "text-slate-600",
    dot: "bg-slate-400",
    cardBg: "bg-slate-50 border-slate-200",
    hint: "Conectá tu cuenta de Mercado Pago para recibir cobros de tus consultas privadas.",
  },
  pending: {
    label: "Verificación en curso",
    tone: "text-amber-700",
    dot: "bg-amber-500",
    cardBg: "bg-amber-50 border-amber-200",
    hint: "Estamos verificando tu cuenta. Te avisamos por email cuando esté lista.",
  },
  connected: {
    label: "Conectada",
    tone: "text-emerald-700",
    dot: "bg-emerald-500",
    cardBg: "bg-emerald-50 border-emerald-200",
    hint: "Los cobros se acreditarán en tu cuenta de Mercado Pago.",
  },
  rejected: {
    label: "Rechazada",
    tone: "text-red-700",
    dot: "bg-red-500",
    cardBg: "bg-red-50 border-red-200",
    hint: "Tu cuenta fue rechazada. Volvé a intentarlo o contactanos.",
  },
};

/**
 * PrivatePracticePanel - 1-click activation of the doctor's "Consultorio Privado".
 * Also owns the MP connection state + SLA config + fee-transparency block.
 */
const PrivatePracticePanel = ({ user, isOpen, onClose, onChanged }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [priceInput, setPriceInput] = useState("");
  const [slaInput, setSlaInput] = useState("");
  const [copied, setCopied] = useState(false);

  // Per-category price overrides (client-side draft). Empty string / undefined
  // means "no override" for that category → falls back to the default price.
  const [categoryPrices, setCategoryPrices] = useState({});
  const [showCategoryPrices, setShowCategoryPrices] = useState(false);

  const [mp, setMp] = useState(null);
  const [mpAlias, setMpAlias] = useState("");
  const [mpSaving, setMpSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("auth_token");
      const [ppRes, mpRes] = await Promise.all([
        fetch(`${API_URL}/api/doctors/me/private-practice`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_URL}/api/doctors/me/mp-account`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      if (ppRes.ok) {
        const d = await ppRes.json();
        setData(d);
        setPriceInput(d.price >= 0 ? String(d.price) : "");
        setSlaInput(d.custom_sla_hours ? String(d.custom_sla_hours) : "");
        // Seed local per-category draft from server. Empty string means "no override".
        const mapped = {};
        const serverMap = d.prices_by_category || {};
        (d.categories || []).forEach((cat) => {
          mapped[cat.id] = serverMap[cat.id] !== undefined && serverMap[cat.id] !== null
            ? String(serverMap[cat.id])
            : "";
        });
        setCategoryPrices(mapped);
        // Auto-expand if the doctor already has any override configured.
        setShowCategoryPrices(Object.keys(serverMap).length > 0);
      } else {
        toast.error("No pudimos cargar tu consultorio privado");
      }
      if (mpRes.ok) {
        const m = await mpRes.json();
        setMp(m);
        setMpAlias(m.alias || "");
      }
    } catch (e) {
      console.error(e);
      toast.error("Error de conexión");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) load();
  }, [isOpen, load]);

  const fullLink = data?.link_path ? `${window.location.origin}${data.link_path}` : null;

  const handleSave = async (active) => {
    const price = priceInput === "" ? null : parseInt(priceInput, 10);
    if (active && (price === null || isNaN(price) || price < 0)) {
      toast.error("Ingresá un precio válido (podés usar 0 para gratis)");
      return;
    }
    const slaMax = data?.sla_max_hours || 360;
    const slaRaw = slaInput === "" ? null : parseInt(slaInput, 10);
    if (slaRaw !== null && (isNaN(slaRaw) || slaRaw < 1 || slaRaw > slaMax)) {
      toast.error(`El SLA debe estar entre 1 y ${slaMax} horas (${Math.round(slaMax / 24)} días)`);
      return;
    }
    // Build per-category diff. Empty string → null (clear override). Numbers →
    // int. Values equal to the default price are still sent as explicit
    // overrides (doctor's intent may be to keep it pinned even if default
    // changes).
    const pricesByCategory = {};
    Object.entries(categoryPrices).forEach(([cat, raw]) => {
      if (raw === "" || raw === null || raw === undefined) {
        pricesByCategory[cat] = null;
      } else {
        const v = parseInt(raw, 10);
        if (!isNaN(v) && v >= 0) pricesByCategory[cat] = v;
      }
    });

    setSaving(true);
    try {
      const token = localStorage.getItem("auth_token");
      const res = await fetch(`${API_URL}/api/doctors/me/private-practice`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          active,
          price: price === null ? null : price,
          custom_sla_hours: slaRaw,
          prices_by_category: pricesByCategory,
        }),
      });
      if (res.ok) {
        const result = await res.json();
        setData((prev) => ({
          ...prev,
          active: result.active,
          price: result.price,
          prices_by_category: result.prices_by_category || {},
        }));
        if (user) user.private_practice_active = result.active;
        toast.success(active ? "¡Consultorio privado activado!" : "Consultorio privado desactivado");
        if (onChanged) onChanged(result);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Error al guardar");
      }
    } catch (e) {
      console.error(e);
      toast.error("Error de conexión");
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async () => {
    if (!fullLink) return;
    await navigator.clipboard.writeText(fullLink);
    setCopied(true);
    toast.success("Enlace privado copiado");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleMpConnect = async () => {
    const alias = mpAlias.trim();
    if (!alias) {
      toast.error("Ingresá tu alias o CVU de Mercado Pago");
      return;
    }
    setMpSaving(true);
    try {
      const token = localStorage.getItem("auth_token");
      const res = await fetch(`${API_URL}/api/doctors/me/mp-account/connect`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ alias }),
      });
      if (res.ok) {
        const r = await res.json();
        setMp((prev) => ({ ...(prev || {}), status: r.status, alias: r.alias }));
        toast.success("Solicitud enviada. Vamos a verificarla en breve.");
      } else {
        toast.error("No pudimos iniciar la conexión");
      }
    } catch (e) {
      console.error(e);
      toast.error("Error de conexión");
    } finally {
      setMpSaving(false);
    }
  };

  const handleMpDisconnect = async () => {
    setMpSaving(true);
    try {
      const token = localStorage.getItem("auth_token");
      const res = await fetch(`${API_URL}/api/doctors/me/mp-account/disconnect`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setMp((prev) => ({ ...(prev || {}), status: "not_connected", alias: null }));
        setMpAlias("");
        toast.success("Cuenta desconectada");
      }
    } catch (e) {
      console.error(e);
    } finally {
      setMpSaving(false);
    }
  };

  // Live fee preview (client-side; matches server logic).
  const price = priceInput === "" ? null : parseInt(priceInput, 10);
  const priceForPreview = price !== null && !isNaN(price) && price >= 0 ? price : null;
  const freeRemaining = mp?.free_consults_remaining ?? 5;
  const feeRate = mp?.fee_rate ?? 0.07;
  const feeCap = mp?.fee_cap ?? 3500;
  const isFreeNext = freeRemaining > 0;
  const serviceFeePreview = isFreeNext || priceForPreview === null || priceForPreview <= 0
    ? 0
    : Math.min(Math.round(priceForPreview * feeRate), feeCap);
  const totalPreview = (priceForPreview ?? 0) + serviceFeePreview;

  const mpStatus = mp?.status || "not_connected";
  const meta = MP_STATUS_META[mpStatus] || MP_STATUS_META.not_connected;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg w-[calc(100vw-2rem)] sm:w-full max-h-[90vh] flex flex-col overflow-hidden p-0">
        <DialogHeader className="flex-shrink-0 px-6 pt-6 pb-3 border-b border-slate-100">
          <DialogTitle className="flex items-center gap-2 pr-8">
            <Briefcase className="w-5 h-5 text-[#1d366b] flex-shrink-0" />
            <span className="truncate">Mi Consultorio Privado</span>
            </DialogTitle>
          </DialogHeader>

          {loading ? (
            <div className="flex items-center justify-center py-10">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1d366b]"></div>
            </div>
          ) : (
            <div className="space-y-5 px-6 py-5 overflow-y-auto min-w-0" data-testid="private-practice-panel">
              <p className="text-sm text-slate-600">
                Atendé a tus pacientes particulares de forma asincrónica con tu propio
                enlace y tu propio precio. Las consultas llegan a tu bandeja
                "Consultorio Privado", separada de las institucionales.
              </p>

              {/* Status */}
              <div
                className={`rounded-lg border px-4 py-3 flex items-center justify-between ${
                  data?.active ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"
                }`}
              >
                <span className="text-sm font-medium text-slate-700">Estado</span>
                <span
                  className={`text-sm font-semibold ${data?.active ? "text-emerald-700" : "text-slate-500"}`}
                  data-testid="private-practice-status"
                >
                  {data?.active ? "Activo" : "Inactivo"}
                </span>
              </div>

              {/* Price */}
              <div>
                <Label htmlFor="private-price">Precio por defecto (ARS) *</Label>
                <div className="flex items-center mt-1">
                  <span className="text-sm text-slate-500 bg-slate-100 px-3 py-2 rounded-l-md border border-r-0 border-slate-200">$</span>
                  <Input
                    id="private-price"
                    type="number"
                    min="0"
                    step="1000"
                    value={priceInput}
                    onChange={(e) => setPriceInput(e.target.value)}
                    placeholder="Ej: 20000 · usá 0 para gratis"
                    className="rounded-l-none"
                    data-testid="private-price-input"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Se aplica a los tipos de consulta que no tengan un precio propio. Podés usar <strong>$0</strong> para dejarla gratis.
                </p>
              </div>

              {/* Per-category prices */}
              {(data?.categories || []).length > 0 && (
                <div className="border border-slate-200 rounded-lg" data-testid="category-prices-block">
                  <button
                    type="button"
                    className="w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 rounded-t-lg"
                    onClick={() => setShowCategoryPrices((v) => !v)}
                    data-testid="toggle-category-prices-btn"
                  >
                    <span className="flex items-center gap-2">
                      <Briefcase className="w-4 h-4 text-[#1d366b]" />
                      Precios por tipo de consulta
                    </span>
                    {showCategoryPrices ? (
                      <ChevronUp className="w-4 h-4 text-slate-500" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-slate-500" />
                    )}
                  </button>
                  {showCategoryPrices && (
                    <div className="border-t border-slate-200 p-3 space-y-2.5">
                      <p className="text-xs text-slate-500">
                        Sobrescribe el precio por defecto para cada tipo. Dejalo vacío para usar el precio por defecto.
                      </p>
                      {(data.categories || []).map((cat) => {
                        const raw = categoryPrices[cat.id] ?? "";
                        const usesDefault = raw === "";
                        return (
                          <div key={cat.id} className="flex items-center gap-2" data-testid={`category-price-row-${cat.id}`}>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-slate-700 truncate">{cat.label}</p>
                              {usesDefault && (
                                <p className="text-[11px] text-slate-400">
                                  Usa el precio por defecto ({priceForPreview !== null ? `$${priceForPreview.toLocaleString("es-AR")}` : "—"})
                                </p>
                              )}
                            </div>
                            <div className="flex items-center flex-shrink-0">
                              <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1.5 rounded-l-md border border-r-0 border-slate-200">$</span>
                              <Input
                                type="number"
                                min="0"
                                step="1000"
                                value={raw}
                                onChange={(e) =>
                                  setCategoryPrices((prev) => ({ ...prev, [cat.id]: e.target.value }))
                                }
                                placeholder="Por defecto"
                                className="rounded-l-none w-24 text-right text-sm h-9"
                                data-testid={`category-price-input-${cat.id}`}
                              />
                              {!usesDefault && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    setCategoryPrices((prev) => ({ ...prev, [cat.id]: "" }))
                                  }
                                  title="Volver al precio por defecto"
                                  className="ml-1 p-1.5 text-slate-400 hover:text-slate-700"
                                  data-testid={`category-price-reset-${cat.id}`}
                                >
                                  <RotateCcw className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Fee preview (transparency) */}
              {priceForPreview !== null && (
                <div className="rounded-lg border border-blue-200 bg-blue-50/60 p-3 text-sm" data-testid="fee-preview-block">
                  <div className="flex items-center gap-2 mb-2 text-blue-900 font-semibold">
                    <Info className="w-4 h-4" />
                    Cómo lo ve el paciente
                  </div>
                  <div className="space-y-1 text-blue-900">
                    <div className="flex justify-between">
                      <span>Tu honorario (por defecto)</span>
                      <span className="font-medium">${(priceForPreview ?? 0).toLocaleString("es-AR")}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="flex items-center gap-1.5">
                        Cargo de servicio
                        {isFreeNext && (
                          <span className="text-[10px] font-semibold text-emerald-800 bg-emerald-100 px-1.5 py-0.5 rounded">
                            PROMO
                          </span>
                        )}
                      </span>
                      <span className="font-medium" data-testid="fee-preview-service">
                        {serviceFeePreview > 0 ? `$${serviceFeePreview.toLocaleString("es-AR")}` : "Sin cargo"}
                      </span>
                    </div>
                    <div className="flex justify-between border-t border-blue-200 pt-1.5 mt-1.5 font-semibold">
                      <span>Total que paga</span>
                      <span data-testid="fee-preview-total">${totalPreview.toLocaleString("es-AR")}</span>
                    </div>
                  </div>
                  {isFreeNext && (
                    <p className="text-xs text-emerald-800 mt-2 flex items-center gap-1.5">
                      <Gift className="w-3.5 h-3.5" />
                      Te quedan {freeRemaining} consulta(s) sin cargo de servicio.
                    </p>
                  )}
                  {!isFreeNext && (
                    <p className="text-xs text-blue-800 mt-2">
                      Cargo de servicio: {(feeRate * 100).toFixed(0)}% (tope ${feeCap.toLocaleString("es-AR")}).
                    </p>
                  )}
                </div>
              )}

              {/* SLA config */}
              <div>
                <Label htmlFor="private-sla" className="flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5" />
                  Plazo máximo de respuesta (horas)
                </Label>
                <Input
                  id="private-sla"
                  type="number"
                  min="1"
                  max={data?.sla_max_hours || 360}
                  value={slaInput}
                  onChange={(e) => setSlaInput(e.target.value)}
                  placeholder="Ej: 48 · máx 360h (15 días)"
                  className="mt-1"
                  data-testid="private-sla-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Tope: {data?.sla_max_hours || 360}h ({Math.round((data?.sla_max_hours || 360) / 24)} días).
                  Dejalo vacío para usar el valor institucional.
                </p>
              </div>

              {/* MP account */}
              <div className={`rounded-lg border p-3 ${meta.cardBg}`} data-testid="mp-account-block">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-slate-700" />
                    <span className="text-sm font-semibold text-slate-800">Mercado Pago</span>
                  </div>
                  <div className={`flex items-center gap-1.5 text-xs font-semibold ${meta.tone}`} data-testid="mp-account-status">
                    <span className={`h-2 w-2 rounded-full ${meta.dot}`} />
                    {meta.label}
                  </div>
                </div>
                <p className="text-xs text-slate-600 mb-2">{meta.hint}</p>

                {mpStatus === "connected" || mpStatus === "pending" ? (
                  <>
                    {mp?.alias && (
                      <div className="text-xs text-slate-700 bg-white/80 rounded px-2 py-1 border border-slate-200 mb-2">
                        <span className="text-slate-500">Alias / CVU:</span>{" "}
                        <span className="font-mono">{mp.alias}</span>
                      </div>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                      onClick={handleMpDisconnect}
                      disabled={mpSaving}
                      data-testid="mp-disconnect-btn"
                    >
                      Desconectar cuenta
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Input
                        placeholder="Alias o CVU de Mercado Pago"
                        value={mpAlias}
                        onChange={(e) => setMpAlias(e.target.value)}
                        className="text-sm"
                        data-testid="mp-alias-input"
                      />
                      <Button
                        onClick={handleMpConnect}
                        disabled={mpSaving}
                        className="bg-[#009EE3] hover:bg-[#008ac7] text-white whitespace-nowrap"
                        data-testid="mp-connect-btn"
                      >
                        {mpSaving ? "Enviando..." : "Conectar"}
                      </Button>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-1.5">
                      Fase 1: los pagos siguen simulados. Cuando habilitemos el cobro real, tu cuenta ya va a estar lista.
                    </p>
                  </>
                )}
              </div>

              {/* Missing slug warning */}
              {!data?.doctor_slug && (
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 text-sm p-3 rounded-lg" data-testid="private-slug-warning">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>
                    Primero configurá tu enlace personalizado desde <strong>"Mi Enlace"</strong> para
                    obtener la URL de tu consultorio privado.
                  </span>
                </div>
              )}

              {/* Private link (when active) */}
              {data?.active && fullLink && (
                <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                  <Label className="text-xs text-slate-500 mb-1.5 block">Tu enlace privado</Label>
                  <div className="bg-white rounded-md border border-slate-200 px-2 py-1.5 text-xs font-mono text-slate-700 break-all" data-testid="private-link-display">
                    {fullLink}
                  </div>
                  <Button
                    className="w-full mt-2.5 bg-green-600 hover:bg-green-700 text-white"
                    onClick={handleCopy}
                    data-testid="copy-private-link-btn"
                  >
                    {copied ? (<><Check className="w-4 h-4 mr-2" />¡Copiado!</>) : (<><Copy className="w-4 h-4 mr-2" />Copiar enlace privado</>)}
                  </Button>
                  <p className="text-xs text-slate-400 mt-2 text-center">
                    El código QR está disponible en "Mi Enlace".
                  </p>
                </div>
              )}

              {/* Actions */}
              {data?.active ? (
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => handleSave(true)}
                    disabled={saving}
                    data-testid="update-private-price-btn"
                  >
                    {saving ? "Guardando..." : "Guardar cambios"}
                  </Button>
                  <Button
                    variant="outline"
                    className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                    onClick={() => handleSave(false)}
                    disabled={saving}
                    data-testid="deactivate-private-btn"
                  >
                    <Power className="w-4 h-4 mr-1.5" />
                    Desactivar
                  </Button>
                </div>
              ) : (
                <Button
                  className="w-full h-11 bg-[#1d366b] hover:bg-[#16295a]"
                  onClick={() => handleSave(true)}
                  disabled={saving || !data?.doctor_slug}
                  data-testid="activate-private-btn"
                >
                  {saving ? "Activando..." : "Activar mi consultorio privado"}
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
  );
};

export default PrivatePracticePanel;
